import { useState } from 'react';
import { authSetPassword } from '../lib/api';
import { KeyRound, Loader2, CheckCircle2 } from 'lucide-react';

/**
 * v5 blok 7 — eigen paswoord instellen/wijzigen, herbruikbaar in klant- en
 * resellerdashboard. Keuze van loginmethode: 'both' (default) of 'password'
 * (mailcode geblokkeerd — aanbevolen voor handelaars: niet iedereen met
 * toegang tot de winkelmailbox mag kunnen inloggen).
 */
export default function PasswordSettings() {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [onlyPassword, setOnlyPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const save = async () => {
    if (pw1.length < 8) { setMsg({ ok: false, text: 'Minstens 8 tekens.' }); return; }
    if (pw1 !== pw2) { setMsg({ ok: false, text: 'Paswoorden komen niet overeen.' }); return; }
    setBusy(true);
    setMsg(null);
    try {
      await authSetPassword(pw1, onlyPassword ? 'password' : 'both');
      setMsg({ ok: true, text: onlyPassword
        ? 'Paswoord ingesteld. Inloggen kan vanaf nu alleen nog met dit paswoord.'
        : 'Paswoord ingesteld. U kan inloggen met paswoord of met een mailcode.' });
      setPw1(''); setPw2('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5">
      <h3 className="text-sm font-bold text-[#1a1a1a] flex items-center gap-2 mb-3">
        <KeyRound size={15} className="text-[#c47a3a]" /> Paswoord instellen / wijzigen
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="password"
          value={pw1}
          onChange={e => { setPw1(e.target.value); setMsg(null); }}
          placeholder="Nieuw paswoord (min. 8 tekens)"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
          autoComplete="new-password"
        />
        <input
          type="password"
          value={pw2}
          onChange={e => { setPw2(e.target.value); setMsg(null); }}
          placeholder="Herhaal paswoord"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
          autoComplete="new-password"
        />
      </div>
      <label className="flex items-center gap-2 mt-3 text-xs text-[#4a4a4a] cursor-pointer select-none">
        <input type="checkbox" checked={onlyPassword} onChange={e => setOnlyPassword(e.target.checked)} className="accent-[#c47a3a]" />
        Alleen paswoord-login toestaan (mailcode uitschakelen — aanbevolen voor handelaars)
      </label>
      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={() => void save()}
          disabled={busy || !pw1 || !pw2}
          className="px-4 py-2 bg-[#c47a3a] hover:bg-[#a8643a] disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5"
        >
          {busy && <Loader2 size={12} className="animate-spin" />} Opslaan
        </button>
        {msg && (
          <p className={`text-xs flex items-center gap-1 ${msg.ok ? 'text-green-700' : 'text-red-600'}`}>
            {msg.ok && <CheckCircle2 size={12} />} {msg.text}
          </p>
        )}
      </div>
    </div>
  );
}
